import type { FableCharacter } from '../api/network_calls';
import characterProfile1 from './images/character_profile_1.jpg';
import characterProfile2 from './images/character_profile_2.jpg';
import characterProfile3 from './images/character_profile_3.jpg';
import characterProfile4 from './images/character_profile_4.jpg';
import characterProfile5 from './images/character_profile_5.jpg';

export const APP_BACKGROUND = '#101219';
export const APP_SURFACE = '#1B1E28';
export const APP_SURFACE_ALT = '#252A36';
export const APP_BORDER = 'rgba(255,255,255,0.12)';
export const APP_TEXT_SECONDARY = 'rgba(255,255,255,0.68)';
export const APP_ACCENT_GOLD = '#D8A363';
export const APP_ACCENT_BLUE = '#76A9E7';
export const APP_LEFT_DRAWER_EXPANDED_WIDTH = 292;
export const APP_LEFT_DRAWER_COLLAPSED_WIDTH = 76;
export const CREATE_STORY_PROMPT_STORAGE_KEY = 'fable-create-story-draft-prompt';
export const VIDEO_MODE_TOOLTIP =
  'Storybook keeps it simple with narration and illustrated pages. Standard Video turns your story into video. Premium Video gives you higher quality AI video with better consistency. Hollywood Video uses our best AI video models for a polished, studio-grade result.';

export type StoryLength = 'Normal' | 'Long' | 'VeryLong';
export type StoryMode = 'Regular' | 'Musical' | 'Dialogue';
export type VideoMode = 'none' | 'regular' | 'premium' | 'hollywood';
export type AudioMode = 'classic' | 'immersive' | 'cinematic';
export const normalizeAudioMode = (value: unknown): AudioMode =>
  value === 'immersive' || value === 'cinematic' ? value : 'classic';
export const audioModeOptions: Array<{ id: AudioMode; label: string }> = [
  { id: 'classic', label: 'Classic' },
  { id: 'immersive', label: 'Immersive' },
  { id: 'cinematic', label: 'Cinematic' },
];
export const AUDIO_MODE_TOOLTIP = 'Classic: Sound effects.\nImmersive: Ambience and sound effects.\nCinematic: Original music throughout the story, ambience and sound effects.';

export type MusicalAudioMode = 'lyrics' | 'instrumental';
export type ImageQuality = 'standard' | 'high' | 'veryHigh';

export type DropdownMediaOption = {
  id: string;
  label: string;
  image?: string;
  defaultLabel?: boolean;
  isSubscriberFeature?: boolean;
};

export type TokenCostsLike = {
  story?: {
    regular?: number;
    musical?: number;
    dialogue?: number;
  };
  addons?: {
    video?: number;
    videoPremium?: number;
    videoHollywood?: number;
    backgroundMusic?: number;
    immersiveAudio?: number;
    cinematicAudio?: number;
    imageQualityHighNormal?: number;
    imageQualityHighLong?: number;
    imageQualityVeryHighNormal?: number;
    imageQualityVeryHighLong?: number;
  };
};

export type CreateStorySettingsState = {
  reader: string;
  storyLength: StoryLength;
  storyMode: StoryMode;
  musicalAudioMode: MusicalAudioMode;
  audioMode: AudioMode;
  imageQuality: ImageQuality;
  storyStyle: string;
  narrator: string;
  storyLanguage: string;
  moral: string;
  mood?: string;
  videoMode: VideoMode;
  includeBackgroundMusic: boolean;
};

export const readerOptions = ['Toddler', 'Teen', 'Adult'] as const;

export const storyLengthOptions: Array<{ id: StoryLength; label: string; detail: string }> = [
  { id: 'Normal', label: 'Normal', detail: '1-2 min' },
  { id: 'Long', label: 'Long', detail: '2-4 min' },
];

export const imageQualityOptions: Array<{ id: ImageQuality; label: string; detail: string }> = [
  { id: 'standard', label: 'Standard', detail: 'Balanced image quality' },
  { id: 'high', label: 'High', detail: 'Sharper story art' },
  { id: 'veryHigh', label: 'Very High', detail: 'Best image fidelity' },
];

export const storyModeOptions: Array<{ id: StoryMode; label: string; detail: string }> = [
  { id: 'Regular', label: 'Narrated', detail: 'One narrator tells the story' },
  { id: 'Dialogue', label: 'Dialogue', detail: 'Characters speak in scenes' },
  { id: 'Musical', label: 'Musical', detail: 'A sung story with a theme' },
];

export const videoModeOptions: Array<{ id: VideoMode; label: string; detail: string }> = [
  { id: 'none', label: 'Storybook', detail: 'Images and audio' },
  { id: 'regular', label: 'Standard Video', detail: 'Story video' },
  { id: 'premium', label: 'Premium Video', detail: 'High quality AI video with better consistency' },
  { id: 'hollywood', label: 'Hollywood Video', detail: 'Best AI video models' },
];

export const musicalAudioModeOptions: Array<{ id: MusicalAudioMode; label: string; detail: string }> = [
  { id: 'lyrics', label: 'Lyrics', detail: 'Sung words' },
  { id: 'instrumental', label: 'Instrumental', detail: 'Music without vocals' },
];

export const styleOptions: DropdownMediaOption[] = [
  { id: '3D Cartoon', label: '3D Cartoon', image: '/art_styles/boy_cartoon.jpeg', defaultLabel: true },
  { id: 'Claymation', label: 'Claymation', image: '/art_styles/boy_claymation.jpeg' },
  { id: 'Childrens Storybook', label: "Children's Book", image: '/art_styles/boy_storybook.jpeg', isSubscriberFeature: true },
  { id: 'Photo Realism', label: 'Realistic', image: '/art_styles/boy_real.jpeg', isSubscriberFeature: true },
  { id: 'Comic Book', label: 'Comic Book', image: '/art_styles/boy_comic.jpeg', isSubscriberFeature: true },
  { id: 'Classic Blocks', label: 'Classic Blocks', image: '/art_styles/boy_lego.jpeg', isSubscriberFeature: true },
  { id: 'Anime', label: 'Animation', image: '/art_styles/boy_anime.jpeg', isSubscriberFeature: true },
  { id: 'Spray Paint', label: 'Spray Paint', image: '/art_styles/boy_spray_paint.jpeg', isSubscriberFeature: true },
  { id: 'Playground Crayon', label: 'Crayon', image: '/art_styles/boy_crayon.jpeg', isSubscriberFeature: true },
  { id: 'Wool Knit', label: 'Cozy Woolknit', image: '/art_styles/boy_woolknit.jpeg', isSubscriberFeature: true },
  { id: 'HD Game', label: 'HD Game', image: '/art_styles/aaa_game.jpeg', isSubscriberFeature: true },
  { id: 'Minecraft', label: 'Blockcraft', image: '/art_styles/boy_mincraft.jpeg', isSubscriberFeature: true },
  { id: 'Water Color', label: 'Watercolor', image: '/art_styles/boy_watercolor.jpeg', isSubscriberFeature: true },
  { id: 'Ink Toon', label: 'Ink Toon', image: '/art_styles/ink_toon.jpeg', isSubscriberFeature: true },
  { id: 'Pixel', label: '2D Game', image: '/art_styles/boy_pixel.jpeg', isSubscriberFeature: true },
  { id: 'Sugarpop', label: 'Sugarpop', image: '/art_styles/boy_sugerpop.jpeg', isSubscriberFeature: true },
  { id: 'Cozy Painting', label: 'Cozy Painting', image: '/art_styles/cozy_paint.jpeg', isSubscriberFeature: true },
  { id: 'Origami', label: 'Origami', image: '/art_styles/boy_origami.jpeg', isSubscriberFeature: true },
  { id: 'Black & White Sketch', label: 'B&W Sketch', image: '/art_styles/boy_sketch.jpeg', isSubscriberFeature: true },
];

export const narratorOptions: DropdownMediaOption[] = [
  { id: 'albus', label: 'Sir Albus', image: '/dropdown-options/narrators/albus.jpeg', defaultLabel: true },
  { id: 'beth', label: 'Aunt Beth', image: '/dropdown-options/narrators/beth.jpeg' },
  { id: 'dash', label: 'Dash', image: '/dropdown-options/narrators/dash.jpeg', isSubscriberFeature: true },
  { id: 'britney', label: 'Britney', image: '/dropdown-options/narrators/britney.jpeg', isSubscriberFeature: true },
  { id: 'malrog', label: 'Malrog', image: '/dropdown-options/narrators/malrog.jpeg', isSubscriberFeature: true },
  { id: 'fiona', label: 'Fiona', image: '/dropdown-options/narrators/fiona.jpeg', isSubscriberFeature: true },
  { id: 'ash', label: 'Ash', image: '/dropdown-options/narrators/ash.jpeg', isSubscriberFeature: true },
  { id: 'juni', label: 'Juni', image: '/dropdown-options/narrators/juni.jpeg', isSubscriberFeature: true },
  { id: 'optimus', label: 'Optimus', image: '/dropdown-options/narrators/optimus.jpeg', isSubscriberFeature: true },
  { id: 'shimmer', label: 'Cherry Twinkel', image: '/dropdown-options/narrators/cherry.jpeg', isSubscriberFeature: true },
  { id: 'coral', label: 'Finn', image: '/dropdown-options/narrators/finn.jpeg', isSubscriberFeature: true },
  { id: 'queen', label: 'Queen Bee', image: '/dropdown-options/narrators/queen.jpeg', isSubscriberFeature: true },
  { id: 'nova', label: 'Penny Snow', image: '/dropdown-options/narrators/nova.jpeg', isSubscriberFeature: true },
  { id: 'arthur', label: 'King Arthur', image: '/dropdown-options/narrators/arthur.jpeg', isSubscriberFeature: true },
  { id: 'walker', label: 'Ms. Walker', image: '/dropdown-options/narrators/walker.jpeg', isSubscriberFeature: true },
  { id: 'juggernaut', label: 'Lord Ragnar', image: '/dropdown-options/narrators/juggernaut.jpeg', isSubscriberFeature: true },
  { id: 'captain', label: 'Captain Flint', image: '/dropdown-options/narrators/captain.jpeg', isSubscriberFeature: true },
  { id: 'sage', label: 'Sage', image: '/dropdown-options/narrators/sage.jpeg', isSubscriberFeature: true },
  { id: 'jane', label: 'Nimble', image: '/dropdown-options/narrators/jane.jpeg', isSubscriberFeature: true },
  { id: 'percy', label: 'Master Percy', image: '/dropdown-options/narrators/percy.jpeg', isSubscriberFeature: true },
  { id: 'william', label: 'Detective Gadget', image: '/dropdown-options/narrators/william.jpeg', isSubscriberFeature: true },
  { id: 'verse', label: 'Atlas', image: '/dropdown-options/narrators/atlas.jpeg', isSubscriberFeature: true },
  { id: 'orin', label: 'Orin Stormvale', image: '/dropdown-options/narrators/orin.jpeg', isSubscriberFeature: true },
  { id: 'onyx', label: 'Boots', image: '/dropdown-options/narrators/boots.jpeg', isSubscriberFeature: true },
  { id: 'fable', label: 'Lucian', image: '/dropdown-options/narrators/rufus.jpeg', isSubscriberFeature: true },
  { id: 'ballad', label: 'Lucky McGuire', image: '/dropdown-options/narrators/lucky.jpeg', isSubscriberFeature: true },
  { id: 'echo', label: 'Dexter Dynamite', image: '/dropdown-options/narrators/miles.jpeg', isSubscriberFeature: true },
  { id: 'anna', label: 'Princess Anna', image: '/dropdown-options/narrators/anna.jpeg', isSubscriberFeature: true },
  { id: 'ruby', label: 'Ruby', image: '/dropdown-options/narrators/samantha.jpeg', isSubscriberFeature: true },
  { id: 'polly', label: 'Polly', image: '/dropdown-options/narrators/polly.jpeg', isSubscriberFeature: true },
  { id: 'alloy', label: 'Empress Khali', image: '/dropdown-options/narrators/khali.jpeg', isSubscriberFeature: true },
  { id: 'amelia', label: 'Amelia', image: '/dropdown-options/narrators/amelia.jpeg', isSubscriberFeature: true },
  { id: 'quinn', label: 'Harvey Quinn', image: '/dropdown-options/narrators/quinn.jpeg', isSubscriberFeature: true },
];

export const languageOptions: DropdownMediaOption[] = [
  { id: 'en', label: 'English', image: '/dropdown-options/languages/english.jpeg', defaultLabel: true },
  { id: 'es', label: 'Spanish', image: '/dropdown-options/languages/spanish.jpeg' },
  { id: 'fr', label: 'French', image: '/dropdown-options/languages/french.jpeg' },
  { id: 'de', label: 'German', image: '/dropdown-options/languages/german.jpeg' },
  { id: 'it', label: 'Italian', image: '/dropdown-options/languages/italian.jpeg' },
  { id: 'pt', label: 'Portuguese', image: '/dropdown-options/languages/portuguese.jpeg' },
  { id: 'ja', label: 'Japanese', image: '/dropdown-options/languages/japan.jpeg' },
  { id: 'ru', label: 'Russian', image: '/dropdown-options/languages/russian.jpeg' },
  { id: 'ar', label: 'Arabic', image: '/dropdown-options/languages/arabic.jpeg' },
  { id: 'hi', label: 'Hindi', image: '/dropdown-options/languages/hindi.jpeg' },
  { id: 'nl', label: 'Dutch', image: '/dropdown-options/languages/dutch.jpeg' },
  { id: 'zh', label: 'Chinese', image: '/dropdown-options/languages/chinese.jpeg' },
  { id: 'tr', label: 'Turkish', image: '/dropdown-options/languages/turkish.jpeg' },
  { id: 'ro', label: 'Romanian', image: '/dropdown-options/languages/romanian.jpeg' },
  { id: 'bg', label: 'Bulgarian', image: '/dropdown-options/languages/bulgarian.jpeg' },
  { id: 'ko', label: 'Korean', image: '/dropdown-options/languages/korean.jpeg' },
  { id: 'uk', label: 'Ukrainian', image: '/dropdown-options/languages/ukranian.jpeg' },
  { id: 'el', label: 'Greek', image: '/dropdown-options/languages/greek.jpeg' },
  { id: 'fi', label: 'Finnish', image: '/dropdown-options/languages/finnish.jpeg' },
  { id: 'id', label: 'Indonesian', image: '/dropdown-options/languages/indonesian.jpeg' },
  { id: 'vi', label: 'Vietnamese', image: '/dropdown-options/languages/vietnamese.jpeg' },
  { id: 'pl', label: 'Polish', image: '/dropdown-options/languages/polish.jpeg' },
  { id: 'th', label: 'Thai', image: '/dropdown-options/languages/thai.jpeg' },
  { id: 'cs', label: 'Czech', image: '/dropdown-options/languages/czech.jpeg' },
];

export const moralOptions: DropdownMediaOption[] = [
  { id: 'None', label: 'No moral, keep it simple', image: '/dropdown-options/morals/none.jpg', defaultLabel: true },
  { id: 'Sharing', label: 'Sharing is caring', image: '/dropdown-options/morals/sharing.jpg' },
  { id: 'Truthfulness', label: 'Always tell the truth', image: '/dropdown-options/morals/truth.jpg' },
  { id: 'Perseverance', label: 'Never give up', image: '/dropdown-options/morals/determination.jpg', isSubscriberFeature: true },
  { id: 'Kindness', label: 'Kindness and compassion', image: '/dropdown-options/morals/kindnesss.jpg', isSubscriberFeature: true },
  { id: 'Mindfulness', label: 'Think before you act', image: '/dropdown-options/morals/mindful.jpg', isSubscriberFeature: true },
  { id: 'Example', label: 'Be a good example for others', image: '/dropdown-options/morals/example.jpg', isSubscriberFeature: true },
  { id: 'Integrity', label: 'Actions speak louder than words', image: '/dropdown-options/morals/actions.jpg', isSubscriberFeature: true },
  { id: 'Forgiveness', label: 'Learn to forgive', image: '/dropdown-options/morals/forgive.jpg', isSubscriberFeature: true },
  { id: 'Respect', label: 'Respect others', image: '/dropdown-options/morals/respect.jpg', isSubscriberFeature: true },
  { id: 'Patience', label: 'Good things take time', image: '/dropdown-options/morals/patience.jpg', isSubscriberFeature: true },
  { id: 'Reliability', label: 'Keep your promises', image: '/dropdown-options/morals/reliable.jpg', isSubscriberFeature: true },
  { id: 'Friendship', label: 'Be a good friend', image: '/dropdown-options/morals/friendship.jpg', isSubscriberFeature: true },
  { id: 'Contentment', label: "You can't always get what you want", image: '/dropdown-options/morals/want.jpg', isSubscriberFeature: true },
  { id: 'Fairness', label: 'Always be fair', image: '/dropdown-options/morals/fair.jpg', isSubscriberFeature: true },
];

/** Legacy stories may store prose here. Pickers and requests only accept catalog values. */
export const normalizeMoral = (value: unknown): string => {
  const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return moralOptions.find(option => option.id.toLowerCase() === candidate || option.label.toLowerCase() === candidate)?.id || 'None';
};

export const musicalMoodOptions: DropdownMediaOption[] = [
  { id: 'happy_energetic', label: 'Happy & Energetic', image: '/dropdown-options/moods/happy_energetic.jpeg' },
  { id: 'suspenseful_thrilling', label: 'Suspenseful & Thrilling', image: '/dropdown-options/moods/suspenseful_thrilling.jpeg' },
  { id: 'calm_serene', label: 'Calm & Serene', image: '/dropdown-options/moods/calm_serene.jpeg' },
  { id: 'romantic_heartfelt', label: 'Romantic & Heartfelt', image: '/dropdown-options/moods/romantic_heartfelt.jpeg', isSubscriberFeature: true },
  { id: 'mysterious_enchanting', label: 'Mysterious & Enchanting', image: '/dropdown-options/moods/mysterious_enchanting.jpeg', isSubscriberFeature: true },
  { id: 'curious_adventurous', label: 'Curious & Adventurous', image: '/dropdown-options/moods/curious_adventurous.jpeg', isSubscriberFeature: true },
  { id: 'peaceful_tranquil', label: 'Peaceful & Tranquil', image: '/dropdown-options/moods/peaceful_tranquil.jpeg', isSubscriberFeature: true },
  { id: 'spooky_mysterious', label: 'Spooky & Mysterious', image: '/dropdown-options/moods/spooky_mysterious.jpeg', isSubscriberFeature: true },
  { id: 'hopeful_inspiring', label: 'Hopeful & Inspiring', image: '/dropdown-options/moods/hopeful_inspiring.jpeg', isSubscriberFeature: true },
];

export const musicalGenreOptions: DropdownMediaOption[] = [
  { id: 'pop', label: 'Pop', image: '/dropdown-options/genres/pop_genre.jpeg' },
  { id: 'country', label: 'Country', image: '/dropdown-options/genres/country_genre.jpeg' },
  { id: 'classical', label: 'Classical', image: '/dropdown-options/genres/classic_genre.jpeg' },
  { id: 'hip_hop', label: 'Hip Hop / Rap', image: '/dropdown-options/genres/rap_genre.jpeg', isSubscriberFeature: true },
  { id: 'rock_roll', label: 'Rock & Roll', image: '/dropdown-options/genres/rock_genre.jpeg', isSubscriberFeature: true },
  { id: 'folk', label: 'Folk', image: '/dropdown-options/genres/folk_genre.jpeg', isSubscriberFeature: true },
  { id: 'electronic', label: 'Dance & Electronic', image: '/dropdown-options/genres/electronic_genre.jpeg', isSubscriberFeature: true },
  { id: 'drum_bass', label: 'Drum & Bass', image: '/dropdown-options/genres/drum_and_bass_genre.jpeg', isSubscriberFeature: true },
  { id: 'k_pop', label: 'K-Pop', image: '/dropdown-options/genres/kpop_genre.jpeg', isSubscriberFeature: true },
  { id: 'alternative', label: 'Alternative', image: '/dropdown-options/genres/alternative_genre.jpeg', isSubscriberFeature: true },
  { id: 'gospel', label: 'Gospel', image: '/dropdown-options/genres/gospel_genre.jpeg', isSubscriberFeature: true },
  { id: 'rnb', label: 'R&B', image: '/dropdown-options/genres/rnb_genre.jpeg', isSubscriberFeature: true },
  { id: 'reggae', label: 'Reggae', image: '/dropdown-options/genres/raggae_genre.jpeg', isSubscriberFeature: true },
  { id: 'jazz', label: 'Jazz', image: '/dropdown-options/genres/jazz_genre.jpeg', isSubscriberFeature: true },
  { id: 'blues', label: 'Blues', image: '/dropdown-options/genres/blues_genre.jpeg', isSubscriberFeature: true },
  { id: 'metal', label: 'Metal', image: '/dropdown-options/genres/metal_genre.jpeg', isSubscriberFeature: true },
];

export const moodOptions: DropdownMediaOption[] = [
  { id: 'auto', label: 'Pick for me', image: '/mic.jpg', defaultLabel: true },
  ...musicalMoodOptions,
  ...musicalGenreOptions,
];

export const getCharacterId = (character: FableCharacter): string =>
  character.characterId || character.id || character.characterName || character.name || '';

export const getCharacterName = (character: FableCharacter): string =>
  character.characterName || character.name || 'Unnamed Character';

const characterFallbackProfiles = [
  characterProfile1,
  characterProfile2,
  characterProfile3,
  characterProfile4,
  characterProfile5,
];

export const getCharacterFallbackAvatar = (characterName?: string | null): string => {
  const hash = (characterName || '').split('').reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );
  return characterFallbackProfiles[hash % characterFallbackProfiles.length];
};

export const getCharacterAvatar = (character: FableCharacter): string =>
  character.images?.[0]
  || character.referenceImages?.[0]
  || character.designPreviewImageUrl
  || getCharacterFallbackAvatar(getCharacterName(character));

export const getCharacterKind = (character: FableCharacter): string =>
  character.kind || 'Other';

export const estimateStoryTokens = (
  storyMode: string,
  storyLength: string,
  includeBackgroundMusic: boolean,
): number => {
  const base =
    storyMode === 'Musical'
      ? 120
      : storyMode === 'Dialogue'
        ? 80
        : 60;
  const lengthMultiplier = storyLength === 'Long' ? 2 : 1;
  return (base * lengthMultiplier) + (includeBackgroundMusic ? 40 : 0);
};

export const getStoryLengthMultiplier = (storyLength: StoryLength): number =>
  storyLength === 'VeryLong' ? 3 : storyLength === 'Long' ? 2 : 1;

export const getImageQualityTokenCost = (
  tokenCosts: TokenCostsLike | undefined,
  storyMode: StoryMode,
  storyLength: StoryLength,
  imageQuality: ImageQuality,
): number => {
  if (imageQuality === 'standard') {
    return 0;
  }

  const costs = tokenCosts?.story || { regular: 60, musical: 80, dialogue: 80 };
  let storyBaseCost = costs.regular || 60;
  if (storyMode === 'Musical') {
    storyBaseCost = costs.musical || 80;
  } else if (storyMode === 'Dialogue') {
    storyBaseCost = costs.dialogue || 80;
  }

  const standardStoryCost = storyBaseCost * getStoryLengthMultiplier(storyLength);
  return imageQuality === 'veryHigh'
    ? standardStoryCost * 2
    : standardStoryCost;
};

export const getVideoModeTokenCost = (
  tokenCosts: TokenCostsLike | undefined,
  videoMode: VideoMode,
): number => {
  if (videoMode === 'premium') {
    return tokenCosts?.addons?.videoPremium || 400;
  }
  if (videoMode === 'hollywood') {
    return tokenCosts?.addons?.videoHollywood || 800;
  }
  return tokenCosts?.addons?.video || 200;
};

export const getAudioModeTokenCost = (
  tokenCosts: TokenCostsLike | undefined, audioMode: AudioMode, storyLength: StoryLength,
): number => {
  const cost = audioMode === 'cinematic' ? tokenCosts?.addons?.cinematicAudio ?? 40
    : audioMode === 'immersive' ? tokenCosts?.addons?.immersiveAudio ?? 20 : 0;
  return cost * getStoryLengthMultiplier(storyLength);
};

export const getV2StoryTokenCost = ({
  tokenCosts,
  storyMode,
  videoMode,
  includeBackgroundMusic,
  audioMode = 'classic',
  storyLength,
  imageQuality,
}: {
  tokenCosts: TokenCostsLike | undefined;
  storyMode: StoryMode;
  videoMode: VideoMode;
  includeBackgroundMusic: boolean;
  audioMode?: AudioMode;
  storyLength: StoryLength;
  imageQuality: ImageQuality;
}): number => {
  const costs = tokenCosts?.story || { regular: 60, musical: 80, dialogue: 80 };
  const videoCost = getVideoModeTokenCost(tokenCosts, videoMode);
  const backgroundMusicCost = tokenCosts?.addons?.backgroundMusic || 40;

  let baseCost = costs.regular || 60;
  if (storyMode === 'Musical') {
    baseCost = costs.musical || 80;
  } else if (storyMode === 'Dialogue') {
    baseCost = costs.dialogue || 80;
  }

  if (videoMode !== 'none') {
    baseCost += videoCost;
  }

  if (includeBackgroundMusic && storyMode !== 'Musical' && audioMode !== 'cinematic') {
    baseCost += backgroundMusicCost;
  }

  if (storyMode !== 'Musical') baseCost += getAudioModeTokenCost(tokenCosts, audioMode, 'Normal');

  return (baseCost * getStoryLengthMultiplier(storyLength))
    + getImageQualityTokenCost(tokenCosts, storyMode, storyLength, imageQuality);
};

export const filterCharactersForStoryCreation = (characters: FableCharacter[]): Partial<FableCharacter>[] =>
  (characters || []).map((character) => {
    const { images, ...rest } = character;
    return rest;
  });

export const createDefaultStorySettings = (
  overrides: Partial<CreateStorySettingsState> = {},
): CreateStorySettingsState => ({
  reader: 'Toddler',
  storyLength: 'Normal',
  storyMode: 'Regular',
  musicalAudioMode: 'lyrics',
  audioMode: 'classic',
  imageQuality: 'standard',
  storyStyle: styleOptions[0].id,
  narrator: narratorOptions[0].id,
  storyLanguage: 'en',
  mood: moodOptions[0].id,
  videoMode: 'none',
  includeBackgroundMusic: false,
  ...overrides,
  moral: normalizeMoral(overrides.moral),
});

export const createClientStoryId = (): string => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getStoryDisplayTitle = (story: any): string => {
  const firstChapter = story?.chapters?.[0];
  const candidates = [
    story?.rootStoryTitle,
    story?.storyTitle,
    story?.storyName,
    story?.chapterTitle,
    story?.title,
    story?.name,
    firstChapter?.rootStoryTitle,
    firstChapter?.storyTitle,
    firstChapter?.storyName,
    firstChapter?.chapterTitle,
    firstChapter?.title,
    firstChapter?.name,
  ];
  const title = candidates.find((value) => typeof value === 'string' && value.trim());
  return title?.trim() || 'Untitled Story';
};

export const getStoryThumbnail = (story: any): string =>
  story?.thumbnailUrl || story?.imageUrls?.[0] || story?.chapters?.[0]?.thumbnailUrl || story?.chapters?.[0]?.imageUrls?.[0] || '';

export const getStoryNarrative = (story: any): string => {
  if (!story) return '';
  if (typeof story.narrative === 'string') return story.narrative;
  if (Array.isArray(story.narrative)) return story.narrative.join('\n');
  if (Array.isArray(story.narrativeParts)) return story.narrativeParts.join('\n');
  if (typeof story.story === 'string') return story.story;
  if (typeof story.text === 'string') return story.text;
  if (typeof story.description === 'string') return story.description;
  return '';
};

export const getBooleanFeatureGate = (
  featureGatesData: any,
  key: string,
  defaultValue = true,
): boolean => {
  const gates = featureGatesData?.featureGates ?? featureGatesData ?? {};
  const value = gates[key];
  return typeof value === 'boolean' ? value : defaultValue;
};
